import { Sol49Service } from '../model/SOL49';
import { CoreService, StamCoreService, TechnicalService } from '../model/CORE';
import AppContext from '../common/context/AppContext';

export class ServiceFactory {
    private static BASE_URL = ServiceFactory.getBaseUrl();
    private static CORE_SERVICE: CoreService;
    private static STAM_CORE_SERVICE: StamCoreService;
    private static TECHNICAL_SERVICE: TechnicalService;
    private static SOL49_SERVICE: Sol49Service;

    public static overrideFetch = () => {
        window.fetch = new Proxy(window.fetch, {
            apply: function (target, that, args) {
                async function md5(source: string) {
                    const sourceBytes = new TextEncoder().encode(source);
                    const digest = await crypto.subtle.digest('SHA-1', sourceBytes);
                    const resultBytes = [...new Uint8Array(digest)];
                    return resultBytes.map(x => x.toString(16).padStart(2, '0')).join('');
                }

                return new Promise((resolve, reject) => {
                    const init: RequestInit = args[1] as RequestInit;
                    const url = args[0] as string;
                    const headers = init && init.headers instanceof Headers ? init.headers : new Headers(init?.headers);
                    headers.append('Gateway-Target', AppContext.isForecast() ? 'pretactical' : 'tactical'); // This one sends body

                    const isRecording = new URLSearchParams(window.location.search).get('record-data') === '1';
                    const isPlayingBack = new URLSearchParams(window.location.search).get('dataset') === 'static';

                    if ((isRecording || isPlayingBack) && url.includes(ServiceFactory.getBaseUrl())) {
                        void md5(
                            init.method.toUpperCase() +
                                url +
                                JSON.stringify(init.body) +
                                (AppContext.isForecast() ? 'pretactical' : 'tactical'),
                        ).then(hash => {
                            const cacheKey = `Static__${hash}`;

                            if (isRecording && isPlayingBack) {
                                reject(new Error('Cannot record and play back at the same time'));
                                return;
                            }

                            // NOTE: If you enable this, it's best to clear your local storage first so you don't see
                            // old cache entries.
                            if (isRecording) {
                                void (target.apply(that, args) as Promise<Response>).then(async x => {
                                    const response = await x.text();

                                    /*
                                        NOTE: This will only work in Firefox since local storage is limited (DOM quota
                                        exceeded) and only there you can up it through dom.storage.default_quota in
                                        about:config.
                                    */
                                    window.localStorage.setItem(cacheKey, response);

                                    console.debug(
                                        init.method.toUpperCase(),
                                        url,
                                        init.body,
                                        AppContext.isForecast() ? 'pretactical' : 'tactical',
                                        cacheKey,
                                        response,
                                    );

                                    (target.apply(that, args) as Promise<Response>)
                                        .then(result => resolve(result))
                                        .catch(reason => reject(reason as Error));
                                });
                            } else if (isPlayingBack) {
                                void (target.apply(that, [`/RecordedDataSet/${cacheKey}.json`]) as Promise<Response>)
                                    .then(cacheFetchResult => {
                                        void cacheFetchResult.text().then(cacheResult => {
                                            console.debug(
                                                `Handing back static data for cache key ${cacheKey}`,
                                                init.method.toUpperCase(),
                                                url,
                                                init.body,
                                                AppContext.isForecast() ? 'pretactical' : 'tactical',
                                            );
                                            resolve(
                                                new Response(cacheResult, {
                                                    status: 200,
                                                    statusText: 'OK',
                                                }),
                                            );
                                        });
                                    })
                                    .catch(error => {
                                        console.error(
                                            'Missing entry in static data cache',
                                            init.method.toUpperCase(),
                                            url,
                                            init.body,
                                            AppContext.isForecast() ? 'pretactical' : 'tactical',
                                            cacheKey,
                                            error,
                                        );

                                        reject(
                                            new Error(
                                                `Cannot replay request with cache key ${cacheKey} as it has not been recorded`,
                                            ),
                                        );
                                    });
                            }
                        });
                    } else {
                        (target.apply(that, args) as Promise<Response>)
                            .then(result => resolve(result))
                            .catch(reason => reject(reason as Error));
                    }
                });
            },
        });
    };

    /**
     * Compute the context path of the deployed webapp, so that it can be set as the base url for subsequent service calls.
     */
    public static getBaseUrl(): string {
        if (!import.meta.env.VITE_API_BASE_URL) {
            throw new Error('No API base URL set. Did you forget to configure it at build time?');
        }

        return import.meta.env.VITE_API_BASE_URL as string;
    }

    public static coreService(): CoreService {
        if (!ServiceFactory.CORE_SERVICE) ServiceFactory.CORE_SERVICE = new CoreService(ServiceFactory.BASE_URL);
        return ServiceFactory.CORE_SERVICE;
    }

    public static technicalService(): TechnicalService {
        if (!ServiceFactory.TECHNICAL_SERVICE) {
            ServiceFactory.TECHNICAL_SERVICE = new TechnicalService(ServiceFactory.BASE_URL);
        }
        return ServiceFactory.TECHNICAL_SERVICE;
    }

    public static stamCoreService(): StamCoreService {
        if (!ServiceFactory.STAM_CORE_SERVICE) {
            ServiceFactory.STAM_CORE_SERVICE = new StamCoreService(ServiceFactory.BASE_URL);
        }
        return ServiceFactory.STAM_CORE_SERVICE;
    }

    public static sol49Service(): Sol49Service {
        if (!ServiceFactory.SOL49_SERVICE) {
            ServiceFactory.SOL49_SERVICE = new Sol49Service(ServiceFactory.BASE_URL);
        }
        return ServiceFactory.SOL49_SERVICE;
    }
}
